import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Box, Typography, Button, Paper, CircularProgress, Alert, IconButton, Dialog, DialogContent } from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VideocamIcon from '@mui/icons-material/Videocam';

export default function TestTaking({ isSequence = false }) {
  const { linkId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [test, setTest] = useState(null);
  const [sequence, setSequence] = useState(null);
  const [currentPassage, setCurrentPassage] = useState(null);
  const [studentName, setStudentName] = useState('');
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [micPermission, setMicPermission] = useState('prompt');
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);
  const [segments, setSegments] = useState([]);
  const [sequenceProgress, setSequenceProgress] = useState({ currentIndex: 0, totalPassages: 0 });
  const [isPaused, setIsPaused] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [lineVideos, setLineVideos] = useState([]);
  const [showingVideo, setShowingVideo] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [heroVideo, setHeroVideo] = useState(null);
  const [showingHeroVideo, setShowingHeroVideo] = useState(false);
  const [heroVideoWatched, setHeroVideoWatched] = useState(false);

  const mediaStreamRef = useRef(null);
  const videoRef = useRef(null);
  const heroVideoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const testStartTimeRef = useRef(null);
  const recordingRef = useRef(false);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isSequence) {
          const sequenceRes = await api.get(`/api/sequences/${linkId}`);
          const sequenceData = sequenceRes.data;
          setSequence(sequenceData);
          setStudentName(sequenceData.childName);
          
          const currentPassageRes = await api.get(`/api/sequences/${linkId}/current-passage`);
          if (currentPassageRes.data.completed) {
            setFinished(true);
            setLoading(false);
            return;
          }
          
          setCurrentPassage(currentPassageRes.data.passage);
          setSequenceProgress({
            currentIndex: currentPassageRes.data.currentIndex,
            totalPassages: currentPassageRes.data.totalPassages
          });
          
          if (currentPassageRes.data.passage.text) {
            const processedSegments = processTextByType(
              currentPassageRes.data.passage.text,
              currentPassageRes.data.passage.passageType || 'assessment'
            );
            setSegments(processedSegments);
            setLineVideos(currentPassageRes.data.passage.lineVideos || []);
            // Set hero video if available
            if (currentPassageRes.data.passage.heroVideo) {
              setHeroVideo(currentPassageRes.data.passage.heroVideo);
              setShowingHeroVideo(true);
            }
          }
        } else {
          const res = await api.get(`/api/tests/${linkId}`);
          setTest(res.data);
          
          let passageText = null;
          let passageType = 'assessment';
          if (res.data.passageId?.text) {
            passageText = res.data.passageId.text;
            passageType = res.data.passageId.passageType || 'assessment';
          } else if (res.data.passage?.text) {
            passageText = res.data.passage.text;
            passageType = res.data.passage.passageType || 'assessment';
          } else if (res.data.text) {
            passageText = res.data.text;
          }
          
          if (passageText) {
            const processedSegments = processTextByType(passageText, passageType);
            setSegments(processedSegments);
            // Get line videos and hero video from passage
            const passageData = res.data.passageId || res.data.passage;
            if (passageData?.lineVideos) {
              setLineVideos(passageData.lineVideos);
            }
            // Set hero video if available
            if (passageData?.heroVideo) {
              setHeroVideo(passageData.heroVideo);
              setShowingHeroVideo(true);
            }
          } else {
            setError('Test passage not found');
          }
          
          if (res.data.childName) {
            setStudentName(res.data.childName);
          }
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(isSequence ? 'Sequence not found' : 'Test not found');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [linkId, isSequence]);

  const processTextByType = (text, passageType) => {
    if (passageType === 'story') {
      // Split by paragraphs (double newlines or single newlines followed by indentation/spacing)
      const paragraphs = text
        .split(/\n\s*\n|\n(?=\s{2,}|\t)/)
        .map(p => p.trim())
        .filter(p => p.length > 0);
      return paragraphs.length > 0 ? paragraphs : [text];
    } else {
      // Split by lines (single newlines, sentences ending with periods, or natural line breaks)
      const lines = text
        .split(/\n|(?<=[.!?])\s+(?=[A-Z])/)
        .map(line => line.trim())
        .filter(line => line.length > 0);
      return lines.length > 0 ? lines : [text];
    }
  };

  // Get video instruction for a specific segment
  const getVideoForSegment = useCallback((segmentIndex) => {
    return lineVideos.find(v => v.lineIndex === segmentIndex);
  }, [lineVideos]);

  // Handle video end - start recording after video finishes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleVideoEnded = useCallback(() => {
    setShowingVideo(false);
    setCurrentVideo(null);
    startRecording();
  }, []);

  // Check for video and either show it or start recording
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const checkAndShowVideoOrRecord = useCallback((segmentIndex) => {
    const video = getVideoForSegment(segmentIndex);
    if (video) {
      setCurrentVideo(video);
      setShowingVideo(true);
    } else {
      startRecording();
    }
  }, [getVideoForSegment]);

  // Skip video and start recording
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSkipVideo = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setShowingVideo(false);
    setCurrentVideo(null);
    startRecording();
  }, []);

  useEffect(() => {
    navigator.permissions?.query({ name: 'microphone' }).then(permissionStatus => {
      setMicPermission(permissionStatus.state);
      permissionStatus.onchange = () => setMicPermission(permissionStatus.state);
    }).catch(() => setMicPermission('prompt'));
  }, []);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      stopAudioLevelMonitoring();
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const requestMic = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      setMicPermission('granted');
      return stream;
    } catch (err) {
      console.error("Microphone permission denied or error:", err);
      setMicPermission('denied');
      return null;
    }
  };

  const handleStart = async () => {
    setError('');
    
    if (!segments || segments.length === 0) {
      setError('No passage content available. Please refresh the page.');
      return;
    }
    
    if (micPermission !== 'granted') {
      const stream = await requestMic();
      if (!stream) {
        setError("Microphone access is required to start the test.");
        return;
      }
    }

    if (!mediaStreamRef.current || !mediaStreamRef.current.active) {
      const stream = await requestMic();
      if (!stream) {
        setError("Microphone access is required to start the test.");
        return;
      }
    }
    
    setStarted(true);
    setCurrentSegmentIndex(0);
    testStartTimeRef.current = Date.now();
    // Check for video instruction before first segment
    checkAndShowVideoOrRecord(0);
  };

  const startAudioLevelMonitoring = (stream) => {
    try {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      
      // Optimized for voice detection
      analyserRef.current.fftSize = 2048;
      analyserRef.current.smoothingTimeConstant = 0.3;
      analyserRef.current.minDecibels = -90;
      analyserRef.current.maxDecibels = -10;
      
      const bufferLength = analyserRef.current.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      const timeDataArray = new Uint8Array(analyserRef.current.fftSize);
      
      const updateAudioLevel = () => {
        if (!analyserRef.current) return;
        
        // Get both frequency and time domain data for better voice detection
        analyserRef.current.getByteFrequencyData(dataArray);
        analyserRef.current.getByteTimeDomainData(timeDataArray);
        
        // Focus on speech frequency range (85Hz - 3400Hz roughly maps to indices 4-170 in a 2048 FFT)
        const speechStart = Math.floor(85 / (audioContextRef.current.sampleRate / 2) * bufferLength);
        const speechEnd = Math.floor(3400 / (audioContextRef.current.sampleRate / 2) * bufferLength);
        const speechData = dataArray.slice(speechStart, speechEnd);
        
        // Calculate RMS (Root Mean Square) for more accurate voice detection
        const rms = Math.sqrt(speechData.reduce((sum, value) => sum + value * value, 0) / speechData.length);
        
        // Also calculate volume from time domain (good for detecting speech patterns)
        const timeRms = Math.sqrt(timeDataArray.reduce((sum, value) => {
          const normalized = (value - 128) / 128;
          return sum + normalized * normalized;
        }, 0) / timeDataArray.length);
        
        // Combine both methods with speech-optimized weighting
        const combinedLevel = (rms * 0.7 + timeRms * 255 * 0.3);
        
        // Apply some smoothing and amplification for better visual response
        const smoothedLevel = combinedLevel * 1.8; // Amplify for better visibility
        
        setAudioLevel(Math.min(255, Math.max(0, smoothedLevel)));
        
        animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
      };
      
      updateAudioLevel();
    } catch (error) {
      console.error('Error setting up audio level monitoring:', error);
    }
  };

  const stopAudioLevelMonitoring = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    setAudioLevel(0);
  };

  const startRecording = () => {
    if (!mediaStreamRef.current || recordingRef.current) return;
    
    try {
      const mediaRecorder = new MediaRecorder(mediaStreamRef.current);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      recordingRef.current = true;
      setIsPaused(false);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start(250);
      startAudioLevelMonitoring(mediaStreamRef.current);
    } catch (error) {
      console.error('Error starting recording:', error);
      setError('Could not start recording. Please refresh and try again.');
    }
  };

  const stopRecording = () => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current || !recordingRef.current) {
        resolve(null);
        return;
      }

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        recordingRef.current = false;
        stopAudioLevelMonitoring();
        resolve(audioBlob);
      };

      mediaRecorderRef.current.stop();
    });
  };

  const handlePause = () => {
    if (recordingRef.current) {
      mediaRecorderRef.current?.pause();
      stopAudioLevelMonitoring();
      setIsPaused(true);
    }
  };

  const handleResume = () => {
    if (recordingRef.current && isPaused) {
      mediaRecorderRef.current?.resume();
      if (mediaStreamRef.current) {
        startAudioLevelMonitoring(mediaStreamRef.current);
      }
      setIsPaused(false);
    }
  };

  const handleNext = async () => {
    const audioBlob = await stopRecording();
    
    if (audioBlob && audioBlob.size > 1000 && segments[currentSegmentIndex]) {
      await submitSegment(currentSegmentIndex, segments[currentSegmentIndex], audioBlob);
    }

    if (currentSegmentIndex < segments.length - 1) {
      const nextIndex = currentSegmentIndex + 1;
      setCurrentSegmentIndex(nextIndex);
      // Check for video instruction before next segment
      checkAndShowVideoOrRecord(nextIndex);
    } else {
      handleFinish();
    }
  };

  const submitSegment = async (segmentIndex, segmentText, audioBlob) => {
    try {
      const formData = new FormData();
      formData.append('sentenceIndex', segmentIndex.toString());
      formData.append('sentenceText', segmentText);
      formData.append('audioFile', audioBlob, `segment-${segmentIndex}.webm`);
      
      if (isSequence) {
        const gradingMethod = sequence.gradingMethod || 'whisper';
        const endpoint = gradingMethod === 'whisper' 
          ? `/api/sequences/${linkId}/whisper-sentence-attempt`
          : `/api/sequences/${linkId}/sentence-attempt`;
        
        const response = await api.post(endpoint, formData, { 
          headers: { 'Content-Type': 'multipart/form-data' } 
        });
        
        if (response.data.testId && !window.currentSequenceTestId) {
          window.currentSequenceTestId = response.data.testId;
        }
      } else {
        const gradingMethod = test.gradingMethod || 'whisper';
        const endpoint = gradingMethod === 'whisper' 
          ? `/api/tests/${linkId}/whisper-sentence-attempt`
          : `/api/tests/${linkId}/sentence-attempt`;
        
        await api.post(endpoint, formData, { 
          headers: { 'Content-Type': 'multipart/form-data' } 
        });
      }
    } catch (error) {
      console.error('Error submitting segment:', error);
    }
  };

  const handleFinish = async () => {
    setSubmitting(true);
    
    // Stop audio monitoring and media stream
    stopAudioLevelMonitoring();
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }

    const totalReadingTimeSeconds = testStartTimeRef.current ? (Date.now() - testStartTimeRef.current) / 1000 : 0;

    try {
      if (isSequence) {
        const testId = window.currentSequenceTestId;
        
        if (testId) {
          await api.post(`/api/sequences/${linkId}/complete-passage`, {
            testId: testId
          });
        }
        
        window.currentSequenceTestId = null;
        const nextPassageRes = await api.get(`/api/sequences/${linkId}/current-passage`);
        
        if (nextPassageRes.data.completed) {
          setFinished(true);
        } else {
          navigate(`/sequence/${linkId}/transition`, { replace: true });
        }
      } else {
        await api.post(`/api/tests/${linkId}/attempt`, {
          studentName: test?.childName || studentName,
          reportDetails: { 
            finalSubmission: true, 
            completedByUser: true, 
            totalReadingTimeSeconds 
          } 
        });
        setFinished(true);
      }
    } catch (error) {
      console.error("Error in final submission:", error);
      setError(isSequence ? 'Failed to submit passage results.' : 'Failed to submit final test results.');
    } finally {
      setSubmitting(false);
    }
  };

  // Enhanced Sound Wave Component
  const SoundWave = () => {
    const level = Math.min(1, Math.max(0.15, audioLevel / 180)); // Optimized for improved audio detection
    const numBars = 5;
    const maxHeight = 24;
    const minHeight = 4;
    
    const bars = Array.from({ length: numBars }, (_, i) => {
      // Create more dynamic wave effect with better responsiveness
      const centerIndex = (numBars - 1) / 2;
      const distanceFromCenter = Math.abs(i - centerIndex);
      const centerMultiplier = 1 - (distanceFromCenter / centerIndex) * 0.3; // Center bars are more prominent
      
      // Add some randomness and wave-like motion
      const timeOffset = Date.now() * 0.003;
      const waveEffect = Math.sin(timeOffset + i * 0.8) * 0.3;
      const randomVariation = Math.sin(timeOffset * 1.5 + i) * 0.2;
      
      // Calculate height with multiple factors
      const baseHeight = level * maxHeight * centerMultiplier;
      const animatedHeight = baseHeight + (waveEffect + randomVariation) * baseHeight * 0.5;
      const finalHeight = Math.max(minHeight, Math.min(maxHeight, animatedHeight));
      
      // Dynamic color based on intensity
      const intensity = finalHeight / maxHeight;
      const getColor = () => {
        if (intensity > 0.7) return '#ff1744'; // Bright red for high intensity
        if (intensity > 0.4) return '#ff5722'; // Orange-red for medium
        return '#ff9800'; // Orange for low intensity
      };

      return (
        <Box
          key={i}
          sx={{
            width: 3,
            height: `${finalHeight}px`,
            background: `linear-gradient(to top, ${getColor()}, ${getColor()}dd)`,
            borderRadius: '2px',
            transition: 'all 0.1s ease-out',
            animation: recordingRef.current && !isPaused && level > 0.2 ? 
              `soundPulse ${0.8 + i * 0.1}s ease-in-out infinite alternate` : 'none',
            '@keyframes soundPulse': {
              '0%': { 
                transform: 'scaleY(0.8)',
                filter: 'brightness(0.9)'
              },
              '100%': { 
                transform: 'scaleY(1.1)',
                filter: 'brightness(1.2)'
              },
            },
          }}
        />
      );
    });

    return (
      <Box 
        sx={{
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          gap: 0.5, 
          padding: '4px 8px',
          background: 'linear-gradient(135deg, rgba(255, 87, 34, 0.08), rgba(255, 23, 68, 0.08))',
          borderRadius: 2,
          border: '1px solid rgba(255, 87, 34, 0.3)',
          minHeight: 28,
          minWidth: 50,
        }}
      >
        {bars}
      </Box>
    );
  };

  const progress = `${currentSegmentIndex + 1} of ${segments.length}`;
  const passageType = isSequence ? 
    (currentPassage?.passageType || 'assessment') : 
    (test?.passageId?.passageType || test?.passage?.passageType || 'assessment');

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh"><CircularProgress /></Box>;
  if (error) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" px={2}><Alert severity="error" sx={{ width: '100%', maxWidth: 500 }}>{error}</Alert></Box>;
  
  if (finished) return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh" bgcolor="#e3fcec" px={2}>
      <Paper sx={{ 
        p: { xs: 3, sm: 4, md: 6 }, 
        textAlign: 'center', 
        maxWidth: 600, 
        width: '100%', 
        borderRadius: 4, 
        boxShadow: '0 8px 32px rgba(79,195,247,0.10)', 
        background: 'linear-gradient(135deg, #e0f7fa 0%, #fffde7 100%)' 
      }}>
        <Typography 
          variant="h2" 
          fontWeight={900} 
          color="#4fc3f7" 
          mb={2}
          sx={{ fontSize: { xs: '2rem', sm: '2.5rem', md: '3.75rem' } }}
        >
          Thank You!
        </Typography>
        <Typography 
          variant="h4" 
          fontWeight={700} 
          color="#81c784" 
          mb={2}
          sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2.125rem' } }}
        >
          You did an amazing job reading!
        </Typography>
        <Typography 
          fontSize={22} 
          color="#333" 
          mb={{ xs: 3, sm: 4 }}
          sx={{ fontSize: { xs: '16px', sm: '18px', md: '22px' } }}
        >
          Your teacher will see your progress soon. Have a wonderful day!
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="large"
          sx={{
            fontSize: { xs: 18, sm: 20, md: 24 },
            py: { xs: 1.5, sm: 2 },
            px: { xs: 4, sm: 5, md: 6 },
            borderRadius: 3,
            fontWeight: 800,
            background: 'linear-gradient(90deg, #4fc3f7 0%, #81c784 100%)',
            width: { xs: '100%', sm: 'auto' },
            maxWidth: { xs: '100%', sm: 400 }
          }}
          onClick={() => {
            // Check if there's a return URL from Willis integration
            const searchParams = new URLSearchParams(location.search);
            const returnUrl = searchParams.get('returnUrl');

            if (returnUrl) {
              // Redirect to Willis with completion status
              window.location.href = `${returnUrl}?status=completed&testId=${test?._id}`;
            } else {
              // Default behavior - go home
              navigate('/');
            }
          }}
        >
          {new URLSearchParams(location.search).get('returnUrl') ? 'Continue' : 'Go Home'}
        </Button>
      </Paper>
    </Box>
  );

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      minHeight: '100vh',
      bgcolor: '#f0fff0',
      px: 2,
      pt: 3,
      pb: 2
    }}>
      {/* Hero Video Dialog - plays before reading starts */}
      <Dialog
        open={showingHeroVideo && !!heroVideo}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            bgcolor: '#000'
          }
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VideocamIcon />
            <Typography variant="h6" fontWeight={700}>
              Watch This First
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setShowingHeroVideo(false);
              setHeroVideoWatched(true);
            }}
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.5)',
              '&:hover': {
                borderColor: 'white',
                bgcolor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            Skip
          </Button>
        </Box>
        <DialogContent sx={{ p: 0, bgcolor: '#000', display: 'flex', justifyContent: 'center' }}>
          {heroVideo && (
            <video
              ref={heroVideoRef}
              src={heroVideo.videoUrl}
              controls
              autoPlay
              onEnded={() => {
                setShowingHeroVideo(false);
                setHeroVideoWatched(true);
              }}
              style={{
                width: '100%',
                maxHeight: '70vh',
                display: 'block'
              }}
            />
          )}
        </DialogContent>
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          py: 2,
          px: 3,
          bgcolor: '#1a1a1a',
          borderTop: '1px solid rgba(255,255,255,0.1)'
        }}>
          <Typography variant="body2" color="rgba(255,255,255,0.7)">
            Watch this introduction, then you'll be ready to read!
          </Typography>
          <Button
            variant="contained"
            onClick={() => {
              setShowingHeroVideo(false);
              setHeroVideoWatched(true);
            }}
            sx={{
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              fontWeight: 700,
              '&:hover': {
                background: 'linear-gradient(135deg, #f57c00 0%, #ef6c00 100%)'
              }
            }}
          >
            Continue
          </Button>
        </Box>
      </Dialog>

      {/* Line Video Instruction Dialog */}
      <Dialog
        open={showingVideo && !!currentVideo}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            bgcolor: '#000'
          }
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VideocamIcon />
            <Typography variant="h6" fontWeight={700}>
              Video Instruction - {passageType === 'story' ? 'Paragraph' : 'Line'} {currentSegmentIndex + 1}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={handleSkipVideo}
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.5)',
              '&:hover': {
                borderColor: 'white',
                bgcolor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            Skip
          </Button>
        </Box>
        <DialogContent sx={{ p: 0, bgcolor: '#000', display: 'flex', justifyContent: 'center' }}>
          {currentVideo && (
            <video
              ref={videoRef}
              src={currentVideo.videoUrl}
              controls
              autoPlay
              onEnded={handleVideoEnded}
              style={{
                width: '100%',
                maxHeight: '70vh',
                display: 'block'
              }}
            />
          )}
        </DialogContent>
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          py: 2,
          px: 3,
          bgcolor: '#1a1a1a',
          borderTop: '1px solid rgba(255,255,255,0.1)'
        }}>
          <Typography variant="body2" color="rgba(255,255,255,0.7)">
            Watch the video, then you'll start reading
          </Typography>
          <Button
            variant="contained"
            onClick={handleSkipVideo}
            sx={{
              background: 'linear-gradient(90deg, #4fc3f7 0%, #81c784 100%)',
              fontWeight: 700,
              '&:hover': {
                background: 'linear-gradient(90deg, #29b6f6 0%, #66bb6a 100%)'
              }
            }}
          >
            Start Reading Now
          </Button>
        </Box>
      </Dialog>

      <Paper sx={{
        borderRadius: 4,
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        width: '100%',
        maxWidth: '900px',
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'center',
        overflow: 'hidden'
      }}>
        {/* Header Section Inside Box */}
        <Box sx={{ 
          bgcolor: 'linear-gradient(135deg, #e3f2fd 0%, #f1f8e9 100%)',
          background: 'linear-gradient(135deg, #e3f2fd 0%, #f1f8e9 100%)',
          p: { xs: 2, sm: 3 },
          borderBottom: '1px solid #e0e0e0'
        }}>
          {(test?.childName || sequence?.childName || studentName) && (
            <Typography 
              variant="h5" 
              color="#4fc3f7" 
              fontWeight={800} 
              sx={{ fontSize: { xs: '18px', sm: '22px', md: '24px' }, mb: 1 }}
            >
              For: {test?.childName || sequence?.childName || studentName}
            </Typography>
          )}
          
          <Typography 
            variant="h4" 
            fontWeight={700} 
            color="#81c784" 
            sx={{ fontSize: { xs: '20px', sm: '24px', md: '28px' }, mb: 1 }}
          >
            {isSequence ? currentPassage?.title : (test?.passageId?.title || test?.passage?.title || 'Reading Test')}
          </Typography>
          
          <Box display="flex" justifyContent="center" alignItems="center" gap={3}>
            {isSequence && (
              <Typography 
                variant="body1" 
                color="#ff9800" 
                fontWeight={600}
                sx={{ fontSize: { xs: '14px', sm: '16px' } }}
              >
                Passage {sequenceProgress.currentIndex + 1} of {sequenceProgress.totalPassages}
              </Typography>
            )}
            {started && (
              <Typography 
                variant="body1" 
                color="#4fc3f7" 
                fontWeight={700}
                sx={{ fontSize: { xs: '14px', sm: '16px' } }}
              >
                {passageType === 'story' ? 'Paragraph' : 'Line'} {progress}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Content Section */}
        <Box sx={{ 
          p: { xs: 2, sm: 3, md: 4 },
          minHeight: '180px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
        {!started ? (
          <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
            <Typography 
              variant="h5" 
              mb={2} 
              color="#666"
              sx={{ fontSize: { xs: '18px', sm: '22px', md: '1.5rem' } }}
            >
              {passageType === 'story' 
                ? 'Read each paragraph aloud, then click Next to continue.' 
                : 'Read each line aloud, then click Next to continue.'
              }
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleStart}
              sx={{ 
                fontSize: { xs: 18, sm: 22, md: 26 }, 
                py: { xs: 2, sm: 2.5 }, 
                px: { xs: 6, sm: 8, md: 10 }, 
                borderRadius: 3, 
                fontWeight: 800, 
                background: 'linear-gradient(90deg, #4fc3f7 0%, #81c784 100%)',
                boxShadow: '0 4px 20px rgba(79,195,247,0.3)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #29b6f6 0%, #66bb6a 100%)',
                  boxShadow: '0 6px 25px rgba(79,195,247,0.4)',
                }
              }}
            >
              Start Reading
            </Button>
            {micPermission === 'denied' && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Microphone access was denied. Please allow microphone access in your browser settings and refresh the page.
              </Alert>
            )}
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center" gap={3} width="100%">
            <Typography
              variant="h4"
              component="p"
              sx={{
                fontSize: { xs: '24px', sm: '32px', md: '40px' },
                lineHeight: { xs: 1.6, sm: 1.7, md: 1.8 },
                fontWeight: 700,
                color: '#333',
                textAlign: 'left',
                padding: '16px 24px',
                background: '#f8f9fa',
                borderRadius: 3,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                border: '2px solid #e9ecef',
                maxWidth: '100%',
                wordBreak: 'break-word',
                hyphens: 'none',
              }}
            >
              {segments[currentSegmentIndex]}
            </Typography>

            <Box display="flex" flexDirection="column" alignItems="center" gap={1.5}>
              {recordingRef.current ? (
                <Box display="flex" flexDirection="column" alignItems="center" gap={1.5}>
                  {/* Status, Sound Waves, and Controls - All inline */}
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: isPaused ? '#ff9800' : '#f44336', 
                        fontWeight: 700,
                        fontSize: { xs: '14px', sm: '16px' },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      🎤 {isPaused ? 'Paused' : 'Recording...'}
                    </Typography>
                    
                    {!isPaused && <SoundWave />}
                    
                    <IconButton
                      onClick={isPaused ? handleResume : handlePause}
                      sx={{
                        backgroundColor: isPaused ? '#4caf50' : '#ff9800',
                        color: 'white',
                        width: 36,
                        height: 36,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                        '&:hover': {
                          backgroundColor: isPaused ? '#388e3c' : '#f57c00',
                          transform: 'scale(1.05)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {isPaused ? <PlayArrowIcon sx={{ fontSize: 20 }} /> : <PauseIcon sx={{ fontSize: 20 }} />}
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <Typography variant="body1" color="#666" textAlign="center" mb={1}>
                  Press Next when you're ready to continue
                </Typography>
              )}
              
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleNext}
                disabled={submitting}
                endIcon={currentSegmentIndex < segments.length - 1 ? <ArrowForwardIcon /> : null}
                sx={{ 
                  fontSize: { xs: 18, sm: 20, md: 22 }, 
                  py: { xs: 1.5, sm: 2 }, 
                  px: { xs: 4, sm: 6, md: 8 }, 
                  borderRadius: 3, 
                  fontWeight: 700, 
                  background: 'linear-gradient(90deg, #4fc3f7 0%, #81c784 100%)',
                  boxShadow: '0 4px 16px rgba(79,195,247,0.3)',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #29b6f6 0%, #66bb6a 100%)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 6px 20px rgba(79,195,247,0.4)',
                  },
                  '&:disabled': {
                    background: '#ccc'
                  }
                }}
              >
                {submitting ? 'Finishing...' : 
                 currentSegmentIndex < segments.length - 1 ? 'Next' : 'Finish'}
              </Button>
            </Box>
          </Box>
        )}
        </Box>
      </Paper>
    </Box>
  );
}